import { useEffect, useRef } from "react";
import QRCode from "qrcode";

type Props = {
  bankBin: string;
  accountNumber: string;
  accountHolder: string;
  amount: number;
  memo: string;
  size?: number;
  className?: string;
};

export function VietQRGenerator({
  bankBin,
  accountNumber,
  accountHolder,
  amount,
  memo,
  size = 240,
  className = "",
}: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const payload = buildVietQRPayload({
      bankBin,
      accountNumber,
      accountHolder,
      amount,
      memo,
    });
    QRCode.toCanvas(ref.current, payload, { width: size, margin: 1 }, (err) => {
      if (err) console.error("QR render", err);
    });
  }, [bankBin, accountNumber, accountHolder, amount, memo, size]);

  return (
    <canvas
      ref={ref}
      className={`mx-auto block h-auto max-w-full rounded-md border ${className}`}
      style={{ width: "100%", maxWidth: size }}
    />
  );
}

export function buildVietQRPayload(p: {
  bankBin: string;
  accountNumber: string;
  accountHolder: string;
  amount: number;
  memo: string;
}): string {
  const tlv = (id: string, value: string) =>
    `${id}${String(value.length).padStart(2, "0")}${value}`;

  const acc = tlv("00", p.bankBin) + tlv("01", p.accountNumber);
  const merchantInfo =
    tlv("00", "A000000727") + tlv("01", acc) + tlv("02", "QRIBFTTA");
  const additional = tlv("08", p.memo);
  const body =
    tlv("00", "01") +
    tlv("01", "11") +
    tlv("38", merchantInfo) +
    tlv("53", "704") +
    tlv("54", String(Math.max(0, Math.round(p.amount)))) +
    tlv("58", "VN") +
    tlv("59", p.accountHolder.slice(0, 25)) +
    tlv("62", additional);

  const withChecksumPlaceholder = body + "6304";
  const crc = crc16Ccitt(withChecksumPlaceholder)
    .toUpperCase()
    .padStart(4, "0");
  return withChecksumPlaceholder + crc;
}

export function crc16Ccitt(s: string): string {
  let crc = 0xffff;
  for (let i = 0; i < s.length; i++) {
    crc ^= s.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1;
      crc &= 0xffff;
    }
  }
  return crc.toString(16);
}
